import React, { useState } from 'react';
import { MdMoreHoriz } from 'react-icons/md';
import {
  Button,
  DialogField,
  DialogFieldLabel,
  DialogFooter,
  DialogSub,
  DialogTextarea,
  Modal,
} from '@aibuild/ui';
import {
  EmailInput,
  EmailMoreButton,
  EmailRow,
} from './ProcessSendCopyDialog.styled';

export interface ProcessSendCopyDialogProps {
  open: boolean;
  onClose: () => void;
  /** Process name — shown in body copy and the success toast. */
  processName: string;
  /**
   * Called with `(email, message)` when the user clicks Send. The dialog
   * does not auto-close; the parent closes it after handling the action
   * (success toast or surfacing an error).
   */
  onSubmit: (email: string, message: string) => void;
  isSending?: boolean;
  dataTestId?: string;
}

const ProcessSendCopyDialog: React.FC<ProcessSendCopyDialogProps> = ({
  open,
  onClose,
  processName,
  onSubmit,
  isSending = false,
  dataTestId = 'process-send-copy-dialog',
}) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // Render-time-derived state: each closed -> open transition wipes the
  // form so a previous send doesn't leak into the next invocation. We
  // run this in render (not in useEffect) to satisfy the
  // `react-hooks/set-state-in-effect` rule.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setEmail('');
      setMessage('');
    }
  }

  const trimmedEmail = email.trim();
  const canSend = !!trimmedEmail && !isSending;

  const commit = () => {
    if (!canSend) return;
    onSubmit(trimmedEmail, message.trim());
  };

  return (
    <Modal
      open={open}
      onClose={isSending ? () => undefined : onClose}
      title="Send copy"
      size="sm"
      dataTestId={dataTestId}
      hideCloseButton={isSending}
      disableEscape={isSending}
      disableBackdropClose={isSending}
      footer={
        <DialogFooter>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSending}
            dataTestId={`${dataTestId}__cancel`}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={commit}
            disabled={!canSend}
            loading={isSending}
            dataTestId={`${dataTestId}__send`}
          >
            Send
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub data-testid={`${dataTestId}__sub`}>
        Send a copy of this Process to a contact. Please note that an Aibuild
        account is required for access. The contact will receive an email
        with instructions to copy the Process,{' '}
        <strong>{processName}</strong> to its Aibuild organization. The link
        will expire in 48 hours.
      </DialogSub>
      <DialogField>
        <DialogFieldLabel htmlFor={`${dataTestId}__email`}>
          Send via email
        </DialogFieldLabel>
        <EmailRow>
          <EmailInput
            id={`${dataTestId}__email`}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSending}
            data-testid={`${dataTestId}__email`}
          />
          <EmailMoreButton
            type="button"
            aria-label="More options"
            data-testid={`${dataTestId}__email-more`}
          >
            <MdMoreHoriz aria-hidden="true" />
          </EmailMoreButton>
        </EmailRow>
      </DialogField>
      <DialogField>
        <DialogFieldLabel htmlFor={`${dataTestId}__message`}>
          Add message
        </DialogFieldLabel>
        <DialogTextarea
          id={`${dataTestId}__message`}
          placeholder="(Optional)"
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isSending}
          data-testid={`${dataTestId}__message`}
        />
      </DialogField>
    </Modal>
  );
};

export default ProcessSendCopyDialog;
